// Sign-in / sign-out signals between the dashboard tabs of one browser profile:
// { type: 'login' | 'logout', uid }. BroadcastChannel where the browser has it,
// otherwise a localStorage write, which the other tabs see as a `storage` event.
export const AUTH_SIGNAL_CHANNEL = 'wlab-auth';
export const AUTH_SIGNAL_STORAGE_KEY = 'wlab:auth-signal';

const hasBroadcastChannel = () => typeof window.BroadcastChannel === 'function';

let channel = null;
const getChannel = () => {
  if (!channel && hasBroadcastChannel()) {
    channel = new window.BroadcastChannel(AUTH_SIGNAL_CHANNEL);
  }
  return channel;
};

// A channel does not hear its own messages, so the sending tab is never told about
// its own sign-out.
export const broadcastAuthSignal = ({ type, uid }) => {
  if (!type || !uid) return;
  const message = { type, uid, at: Date.now() };
  const broadcast = getChannel();
  if (broadcast) {
    broadcast.postMessage(message);
    return;
  }
  try {
    // A fresh value every time, so the same signal twice still fires `storage`.
    window.localStorage.setItem(
      AUTH_SIGNAL_STORAGE_KEY,
      JSON.stringify({ ...message, nonce: Math.random() })
    );
  } catch (e) {
    // Storage blocked: the other tabs notice on their own account check.
  }
};

const parseStored = value => {
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};

// Calls handler(signal) for every signal from another tab; returns the unsubscribe.
export const listenAuthSignals = handler => {
  const broadcast = getChannel();
  const onMessage = event => handler(event.data);
  const onStorage = event => {
    if (event.key !== AUTH_SIGNAL_STORAGE_KEY || !event.newValue) return;
    const signal = parseStored(event.newValue);
    if (signal) handler(signal);
  };
  broadcast?.addEventListener('message', onMessage);
  window.addEventListener('storage', onStorage);
  return () => {
    broadcast?.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
};

// Tests only.
export const resetAuthSignalChannel = () => {
  channel?.close();
  channel = null;
};
